import {
  createFeatureSelector,
  createReducer,
  createSelector,
  on,
} from '@ngrx/store';
import * as UserActions from './user.actions';
import { Customer } from 'src/app/core/models/customer.model';
import { BaseUser } from 'src/app/core/models/base-user.model';
import { Administrator } from 'src/app/core/models/administrator.model';
import {AllauthError} from '../../core/models/allauth.model';

export const userFeatureKey = 'profile';

export interface Credentials {
  username: string;
  password: string;
}

export interface LoginResponse {
  user: any;
  token: string;
  sessionToken: string;
}

export interface UserState {
  user: BaseUser;
  token: string;
  sessionToken: string;
  tokenRefreshInterval: number;
  tokenRefreshFailureMessage: string;
  mfaAuthenticationError: AllauthError;
  reauthenticationError: AllauthError;
  changePasswordError: AllauthError;
  authenticated: boolean;
  loginError: AllauthError;
  customer: Customer;
  customerPermissions: string[];
  administrators: Administrator[];
  loggingIn: boolean;
}

const initialState: UserState = {
  user: undefined,
  token: '',
  sessionToken: '',
  tokenRefreshInterval: 360000,
  tokenRefreshFailureMessage: '',
  authenticated: false,
  loginError: undefined,
  mfaAuthenticationError: undefined,
  reauthenticationError: undefined,
  changePasswordError: undefined,
  customer: undefined,
  customerPermissions: [],
  administrators: [],
  loggingIn: false,
};

export const userReducer = createReducer<UserState>(
  initialState,
  on(UserActions.login, (state) => ({
    ...state,
    loggingIn: true,
    // Clear the previous failure so a stale message doesn't sit under the
    // form while the new attempt is in flight
    loginError: undefined,
  })),
  on(UserActions.loginSuccess, (state, loginResponse: LoginResponse) => ({
    ...state,
    loggingIn: false,
    authenticated: true,
    user: loginResponse.user,
    token: loginResponse.token,
    sessionToken: loginResponse.sessionToken,
    loginError: undefined,
    reauthenticationError: undefined,
    mfaAuthenticationError: undefined,
    changePasswordError: undefined,
  })),
  on(UserActions.storeSessionToken, (state, action) => ({
    ...state,
    sessionToken: action.sessionToken,
  })),
  on(UserActions.loginFailure, (state, action) => ({
    ...state,
    loggingIn: false,
    loginError: action.error,
  })),
  // Login can also end by handing off to the MFA step
  on(UserActions.totpLoginRedirect, (state) => ({
    ...state,
    loggingIn: false,
  })),
  on(UserActions.mfaAuthenticationFailure, (state, action) => ({
    ...state,
    mfaAuthenticationError: action.error
  })),
  on(UserActions.reauthenticateFailure, (state, action) => ({
    ...state,
    reauthenticationError: action.error
  })),
  on(UserActions.changePasswordFailure, (state, action) => ({
    ...state,
    changePasswordError: action.error
  })),
  on(UserActions.refreshTokenSuccess, (state, action) => {
    return { ...state, token: action.token };
  }),
  on(UserActions.refreshTokenFailure, (state, action) => {
    return {
      ...state,
      tokenRefreshFailureMessage:
        action.error?.message ?? 'Failed to refresh token',
    };
  }),
  on(UserActions.getCustomerSuccess, (state, customer: Customer) => ({
    ...state,
    customer,
  })),
  on(UserActions.getCustomerFailure, (state) => ({
    ...state,
    failureMessage: 'Failed to get customer',
  })),
  on(UserActions.getCustomerPermissionsSuccess, (state, action) => ({
    ...state,
    customerPermissions: [...action.permissions],
  })),
  on(UserActions.getCustomerPermissionsFailure, (state) => ({
    ...state,
    failureMessage: 'Failed to get customer permissions',
  })),
  on(UserActions.getAdministratorsSuccess, (state, action) => ({
    ...state,
    administrators: action.administrators,
  })),
  on(UserActions.getAdministratorsFailure, (state) => ({
    ...state,
    failureMessage: 'Failed to get administrators',
  }))
);

// Selectors

const getUserFeatureState = createFeatureSelector<UserState>('profile');

export const getCurrentUser = createSelector(
  getUserFeatureState,
  (state) => state.user
);

export const getCurrentCustomer = createSelector(
  getUserFeatureState,
  (state) => state.customer
);

export const getAdministrators = createSelector(
  getUserFeatureState,
  (state) => state.administrators
);

export const getToken = createSelector(
  getUserFeatureState,
  (state) => state.token
);

export const getProfile = createSelector(getUserFeatureState, (state) => state);

export const getLoginError = createSelector(
  getUserFeatureState,
  (state) => state.loginError
);

export const getLoggingIn = createSelector(
  getUserFeatureState,
  (state) => state.loggingIn
);

export const getMfaAuthenticationError = createSelector(
  getUserFeatureState,
  (state) => state.mfaAuthenticationError
);

export const getReauthenticationError = createSelector(
  getUserFeatureState,
  (state) => state.reauthenticationError
);

export const getChangePasswordError = createSelector(
  getUserFeatureState,
  (state) => state.changePasswordError
);
